import { useMemo } from 'react';
import { FunctionField, usePermissions, useTranslate } from 'react-admin';
import { Box, Typography, useTheme, useMediaQuery, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { formatIntervalSince } from '@/helpers/date/formatTimeInterval.js';
import CustomList from '@/components/global/list/CustomList.jsx';
import { styled } from '@mui/material/styles';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import CustomDatagridHeader from '@/components/global/list/CustomDatagridHeader';
import TableHeaderCellWithSubtitle from '@/components/global/fields/TableHeaderCellWithSubtitle';
import { useDrawer } from '@/composables/useDrawer';
import { Palette } from '@/themes/themeOverrides/Palette';
import { getUserFilters } from '@/components/users/filters/userFilters.jsx';
import { UserInfoField } from '@/components/users/fields/UserInfoField.jsx';
import { UserRoleField } from '@/components/users/fields/UserRoleField.jsx';
import { UserContractorsField } from '@/components/users/fields/UserContractorsField.jsx';
import { UserListActions } from '@/components/users/buttons/UserListActions.jsx';
import { UserMoreActions } from '@/components/users/buttons/UserMoreActions.jsx';
import { userListStyles } from '@/themes/commonStyles/user.js';

export { UserCreate } from '@/components/users/forms/UserCreate.jsx';
export { UserEdit } from '@/components/users/forms/UserEdit.jsx';

const StyledUserList = styled(Box)(userListStyles);

export const UserList = () => {
    const { isLoading, permissions } = usePermissions();
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { openDrawer } = useDrawer();

    const isAdminOrBok = permissions.isAdmin || permissions.isBok;

    const filters = useMemo(
        () =>
            getUserFilters({
                isAdminOrBok,
                isClientMaster: permissions.isClientMaster,
                isServiceMaster: permissions.isServiceMaster,
                translate,
                isMobile,
            }),
        [isAdminOrBok, permissions.isClientMaster, permissions.isServiceMaster, translate, isMobile],
    );

    const inactiveUserRowStyles = record => ({
        '& .MuiTableCell-root.MuiTableCell-body': {
            backgroundColor: record.isActive ? Palette.white : Palette.gray.gray50,
        },
        '& .MuiChip-root': {
            backgroundColor: !record.isActive && Palette.gray.gray150,
        },
        '& .user-inactive': {
            color: Palette.gray.gray500,
        },
        '& .datagrid-actions-container': {
            '& .MuiButtonBase-root': {
                backgroundColor: record.isActive ? Palette.white : Palette.gray.gray50,
            },
        },
    });

    if (isLoading) return null;

    return (
        <StyledUserList>
            <CustomList filters={filters} actions={<UserListActions />} sort={{ field: 'updatedAt', order: 'DESC' }} addLastColumnShadow={true}>
                <CustomDatagridConfigurable
                    bulkActionButtons={false}
                    header={<CustomDatagridHeader />}
                    rowSx={inactiveUserRowStyles}
                    rowClick={id => {
                        openDrawer(id, 'user-show');
                        return false;
                    }}
                >
                    <FunctionField
                        label={<TableHeaderCellWithSubtitle title={translate('resources.user.labels.userInfoTitle')} subTitle={translate('common.contact')} />}
                        sortBy='firstName'
                        render={record => <UserInfoField record={record} />}
                    />
                    {isAdminOrBok && (
                        <FunctionField
                            label={<TableHeaderCellWithSubtitle title={translate('common.contractor')} />}
                            sortBy='contractorIds'
                            render={() => <UserContractorsField sortBy='contractorIds' />}
                        />
                    )}
                    <FunctionField
                        label={<TableHeaderCellWithSubtitle title={translate('resources.user.labels.roleSection')} />}
                        sortBy='role'
                        render={record => <UserRoleField record={record} />}
                    />
                    <FunctionField
                        label={<TableHeaderCellWithSubtitle title={translate('resources.user.labels.lastActivity')} />}
                        source='updatedAt'
                        sortBy='updatedAt'
                        render={record => {
                            if (!record.isActive) {
                                return (
                                    <Typography component='p' className='user-inactive'>
                                        {translate('resources.user.labels.inactive')}
                                    </Typography>
                                );
                            }
                            if (!record.updatedAt) {
                                return '';
                            }
                            return (
                                <Tooltip
                                    title={
                                        formatIntervalSince(record.updatedAt)
                                            ? `${formatIntervalSince(record.updatedAt)} ${translate('notification.timeAgo')}`
                                            : ''
                                    }
                                    arrow
                                    classes={{ tooltip: 'tooltip-black' }}
                                >
                                    <span>{format(new Date(record.updatedAt), 'dd.MM.yyyy HH:mm')}</span>
                                </Tooltip>
                            );
                        }}
                    />
                    <FunctionField
                        label={<TableHeaderCellWithSubtitle title={translate('common.createdAt')} />}
                        source='createdAt'
                        sortBy='createdAt'
                        render={record =>
                            record.createdAt ? (
                                <Tooltip
                                    title={
                                        formatIntervalSince(record.createdAt)
                                            ? `${formatIntervalSince(record.createdAt)} ${translate('notification.timeAgo')}`
                                            : ''
                                    }
                                    arrow
                                    classes={{ tooltip: 'tooltip-black' }}
                                >
                                    <span>{format(new Date(record.createdAt), 'dd.MM.yyyy HH:mm')}</span>
                                </Tooltip>
                            ) : (
                                ''
                            )
                        }
                    />
                    <FunctionField
                        label={translate('common.options')}
                        render={record => (
                            <Box onClick={event => event.stopPropagation()}>
                                <UserMoreActions permissions={permissions} record={record} />
                            </Box>
                        )}
                    />
                </CustomDatagridConfigurable>
            </CustomList>
        </StyledUserList>
    );
};
